import { useCallback, useEffect } from 'react'
import { useSetRecoilState } from 'recoil'

import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import type { ItemControlState } from '@/types/itemControls'

type PartialControlState = {
  type?: ItemControlState['type']
  hideInPdf?: ItemControlState['hideInPdf']
  position?: Partial<ItemControlState['position']>
  selection?: Partial<ItemControlState['selection']>
  image?: Partial<ItemControlState['image']>
  text?: Partial<ItemControlState['text']>
  warnings?: Partial<ItemControlState['warnings']>
}

/**
 * Hook for items to register their control state with the ItemControlOverlay.
 *
 * Items call this hook with their current state, and the hook updates the
 * itemControlStateAtomFamily atom. The ItemControlOverlay reads from this
 * atom to render controls above all items.
 *
 * @param itemId - The unique ID of the item
 * @param controlState - The control state to register (nested structure)
 */
export const useItemControlState = (
  itemId: string,
  controlState: PartialControlState,
) => {
  const setControlState = useSetRecoilState(itemControlStateAtomFamily(itemId))

  // Update control state whenever it changes
  useEffect(() => {
    if (!itemId) return

    setControlState((prev) => ({
      ...prev,
      type: controlState.type ?? prev.type,
      hideInPdf: controlState.hideInPdf ?? prev.hideInPdf,
      position: {
        ...prev.position,
        ...controlState.position,
      },
      selection: {
        ...prev.selection,
        ...controlState.selection,
      },
      image: controlState.image
        ? {
            ...prev.image,
            ...controlState.image,
          }
        : prev.image,
      text: controlState.text
        ? {
            ...prev.text,
            ...controlState.text,
          }
        : prev.text,
      warnings: controlState.warnings
        ? {
            ...prev.warnings,
            ...controlState.warnings,
          }
        : prev.warnings,
    }))
  }, [itemId, controlState, setControlState])

  // Cleanup on unmount - mark as not selected
  useEffect(() => {
    return () => {
      if (itemId) {
        setControlState((prev) => ({
          ...prev,
          selection: {
            ...prev.selection,
            selected: false,
          },
        }))
      }
    }
  }, [itemId, setControlState])

  // Function to update specific control state properties
  const updateControlState = useCallback(
    (updates: PartialControlState) => {
      setControlState((prev) => ({
        ...prev,
        type: updates.type ?? prev.type,
        hideInPdf: updates.hideInPdf ?? prev.hideInPdf,
        position: updates.position
          ? { ...prev.position, ...updates.position }
          : prev.position,
        selection: updates.selection
          ? { ...prev.selection, ...updates.selection }
          : prev.selection,
        image: updates.image ? { ...prev.image, ...updates.image } : prev.image,
        text: updates.text ? { ...prev.text, ...updates.text } : prev.text,
        warnings: updates.warnings
          ? { ...prev.warnings, ...updates.warnings }
          : prev.warnings,
      }))
    },
    [setControlState],
  )

  return { updateControlState }
}
